import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FaTrophy, FaClock, FaMedal, FaUsers, FaPlay,
  FaLock, FaCrown
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Avatar from '../components/UI/Avatar';
import Button from '../components/UI/Button';
import PageTransition from '../components/UI/PageTransition';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';


function useCountdown(endsAt) {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const end = typeof endsAt === 'number' ? endsAt : new Date(endsAt).getTime();
    const update = () => {
      const diff = end - Date.now();
      if (diff <= 0) { setTimeLeft('Terminé'); clearInterval(t); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return timeLeft;
}

function TournamentCard({ tournament, onJoin }) {
  const countdown = useCountdown(tournament.endsAt);
  const fillPercent = Math.min(
    100,
    Math.round((tournament.participants / Math.max(1, tournament.maxParticipants)) * 100)
  );

  return (
    <motion.div
      className="card border-green-800"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className="text-white font-black text-base"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {tournament.nom}
            </h3>
            <span
              className="bg-qfl-gold/20 text-qfl-gold text-xs px-2 py-0.5 rounded-full border border-qfl-gold/30 font-bold uppercase"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              Premium
            </span>
          </div>
          <p className="text-white/50 text-xs">{tournament.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
          <div className="flex items-center gap-1 text-orange-400 text-xs font-semibold">
            <FaClock size={10} />
            {countdown}
          </div>
          <div className="text-white/40 text-xs flex items-center gap-1">
            <FaUsers size={10} />
            {tournament.participants}/{tournament.maxParticipants}
          </div>
        </div>
      </div>

      <div className="h-1.5 bg-white/10 rounded-full mb-3 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${fillPercent}%`,
            background: fillPercent > 80 ? '#ef4444' : fillPercent > 50 ? '#f97316' : '##003DA5',
          }}
        />
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {tournament.prix.slice(0, 3).map((p) => (
          <div key={p.place} className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1">
            <span className="text-sm">{p.icon}</span>
            <span className="text-white/60 text-xs">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Message "tournoi pas encore commencé" */}
      {tournament.statut === 'bientot' && (
        <div className="rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}>
          <span className="text-yellow-400 text-sm flex-shrink-0">⏳</span>
          <p className="text-yellow-400/80 text-xs leading-relaxed">
            <strong>Le tournoi n'a pas encore commencé.</strong> Tu peux t'inscrire maintenant,
            mais tes parties ne seront comptabilisées qu'à partir du{' '}
            <strong>{new Date(tournament.startsAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</strong>.
          </p>
        </div>
      )}

      {/* Frais d'entrée */}
      {tournament.entry_fee > 0 && (
        <div className="flex items-center justify-between rounded-lg px-3 py-2 mb-3"
          style={{ background: 'rgba(255,184,28,0.08)', border: '1px solid rgba(255,184,28,0.2)' }}>
          <span className="text-white/50 text-xs">Frais d'entrée</span>
          <span className="text-qfl-gold font-black text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            🪙 {tournament.entry_fee} pièces
          </span>
        </div>
      )}

      <Button
        variant={tournament.statut === 'actif' ? 'gold' : 'secondary'}
        size="md"
        fullWidth
        disabled={tournament.statut === 'termine'}
        onClick={() => onJoin(tournament)}
      >
        {tournament.statut === 'bientot' ? (
          tournament.entry_fee > 0
            ? <><FaClock size={14} /> S'INSCRIRE · 🪙 {tournament.entry_fee}</>
            : <><FaClock size={14} /> S'INSCRIRE</>
        ) : tournament.entry_fee > 0 ? (
          <><FaPlay size={14} /> REJOINDRE · 🪙 {tournament.entry_fee}</>
        ) : (
          <><FaPlay size={14} /> REJOINDRE</>
        )}
      </Button>
    </motion.div>
  );
}

function PremiumGate() {
  const navigate = useNavigate();
  return (
    <PageTransition variant="slideIn">
      <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
        <div className="flex items-center justify-center gap-3 pt-8 pb-4 px-4">
          <FaTrophy
            size={26}
            className="text-qfl-gold"
            style={{ filter: 'drop-shadow(0 0 10px rgba(255,184,28,0.4))' }}
          />
          <h1
            className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            TOURNOIS
          </h1>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 pb-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-sm text-center"
          >
            <div
              className="rounded-3xl overflow-hidden border border-yellow-500/30 p-8"
              style={{
                background: 'linear-gradient(135deg, rgba(255,184,28,0.12) 0%, rgba(0,61,165,0.8) 100%)',
                boxShadow: '0 0 40px rgba(255,184,28,0.1)',
              }}
            >
              <FaCrown
                size={52}
                className="text-qfl-gold mx-auto mb-4"
                style={{ filter: 'drop-shadow(0 0 14px rgba(255,184,28,0.5))' }}
              />
              <h2
                className="text-2xl font-black uppercase tracking-widest text-qfl-gold mb-2"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Réservé Premium
              </h2>
              <p className="text-white/60 text-sm mb-6">
                Les tournois sont exclusifs aux membres Premium. Rejoins la compétition et gagne des récompenses uniques.
              </p>
              <Button variant="gold" size="lg" fullWidth onClick={() => navigate('/shop')}>
                <FaCrown size={16} />
                DEVENIR PREMIUM
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </PageTransition>
  );
}

function Tournament() {
  const { user, updateUser } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const isPremium = user?.plan === 'premium' || user?.role === 'admin';

  // Rafraîchit le profil depuis la DB au chargement — évite le token stale
  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      if (data.user) updateUser(data.user);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isPremium) return;
    api.get('/tournaments')
      .then(({ data }) => setTournaments(data.tournaments || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, [isPremium]);

  if (!isPremium) {
    return <PremiumGate />;
  }

  const handleJoin = async (tournament) => {
    if (tournament.entry_fee > 0) {
      const ok = window.confirm(
        `⚠️ L'inscription à "${tournament.nom}" coûte ${tournament.entry_fee} pièces.\n\nConfirmer ?`
      );
      if (!ok) return;
    }
    try {
      await api.post(`/tournaments/${tournament._id}/join`);
      if (tournament.entry_fee > 0) {
        toast.success(`Inscrit ! 🪙 ${tournament.entry_fee} pièces déduites.`);
        updateUser({ ...user, coins: (user?.coins || 0) - tournament.entry_fee });
      } else {
        toast.success(`Inscrit à "${tournament.nom}" !`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Erreur lors de l\'inscription');
      if (err?.response?.status !== 409) return;
    }

    // Charger le classement
    try {
      const { data } = await api.get(`/tournaments/${tournament._id}/leaderboard`);
      setLeaderboard(data.leaderboard || []);
    } catch {
      setLeaderboard([]);
    }
    setSelectedTournament(tournament);
  };

  return (
    <PageTransition variant="slideIn">
      <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
        <div className="flex items-center justify-center gap-3 pt-8 pb-4 px-4">
          <FaTrophy
            size={26}
            className="text-qfl-gold"
            style={{ filter: 'drop-shadow(0 0 10px rgba(255,184,28,0.4))' }}
          />
          <h1
            className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            TOURNOIS
          </h1>
        </div>

        <div className="flex-1 overflow-y-auto px-4 flex flex-col gap-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
            </div>
          ) : tournaments.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <FaTrophy size={40} className="text-white/15 mb-4" />
              <p
                className="text-white/40 font-bold uppercase tracking-widest text-sm mb-1"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Aucun tournoi en cours
              </p>
              <p className="text-white/25 text-xs">Reviens bientôt pour participer aux prochains tournois</p>
            </motion.div>
          ) : (
            tournaments.map((t) => (
              <TournamentCard key={t._id} tournament={t} onJoin={handleJoin} />
            ))
          )}

          {selectedTournament && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <h3
                className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                <FaMedal className="inline mr-2" />
                CLASSEMENT — {selectedTournament.nom}
              </h3>

              <div className="flex flex-col gap-2 pb-4">
                {leaderboard.map((player, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                      player.isMe
                        ? 'bg-qfl-gold/10 border-qfl-gold/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <span
                      className="w-6 text-center font-black text-sm"
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        color: i === 0 ? '#FFB81C' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#6b7280',
                      }}
                    >
                      {i + 1}
                    </span>
                    <Avatar user={player} size="xs" />
                    <div className="flex-1">
                      <span
                        className={`font-bold text-sm ${player.isMe ? 'text-qfl-gold' : 'text-white'}`}
                        style={{ fontFamily: "'Rajdhani', sans-serif" }}
                      >
                        {player.pseudo}
                      </span>
                    </div>
                    <span
                      className="font-black text-qfl-gold"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}
                    >
                      {player.score} pts
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PageTransition>
  );
}

export default Tournament;


